import { EventDispatcher } from '@ribajs/events'
import { ShopifyProductService } from './shopify-product.service.js'

import type {
  ShopifyProduct,
  CustomerFavorite,
  CustomerFavoriteType,
  CustomerFavoriteProduct,
  CustomerFavoriteData,
} from '../types/index.js'

export class CustomerFavoritesService {
  /** The event id */
  public static eventsId = 'CustomerFavorites'
  /**
   * The event name emitted when a favorite is added
   * @event CustomerFavorites:add
   */
  public static addEventName = `${this.eventsId}:add`
  /**
   * The event name emitted when a favorite is updated
   * @event CustomerFavorites:update
   */
  public static updateEventName = `${this.eventsId}:update`
  /**
   * The event name emitted when a favorite is removed
   * @event CustomerFavorites:remove
   */
  public static removeEventName = `${this.eventsId}:remove`
  /**
   * The event name emitted when a favorite is cleared
   * @event CustomerFavorites:clear
   */
  public static clearEventName = `${this.eventsId}:clear`
  /**
   * The event name emitted when a favorites are changed
   * @event CustomerFavorites:changed
   */
  public static changedEventName = `${this.eventsId}:changed`
  /**
   * Events for the favorites
   */
  public static events = new EventDispatcher(this.eventsId)

  /**
   * Returns the cache key for the given type and handle
   * @param type The type of the favorite
   * @param handle The handle of the favorite
   * @returns The cache key
   */
  public static getCacheKey(type: CustomerFavoriteType, handle: string) {
    return `favorite-${type}-${handle}`
  }

  /**
   * Add a 'product' | 'collection' | 'page' | 'blog' | 'article' to the favorites
   * @param type The type of the favorite
   * @param handle The handle of the favorite
   * @param variantId The id of the current selected variant
   */
  static async add(
    type: CustomerFavoriteType,
    handle: string,
    additionalData: CustomerFavoriteData = {},
  ) {
    const favorite: CustomerFavorite = {
      type,
      handle,
      data: additionalData,
    }
    window.localStorage.setItem(
      this.getCacheKey(type, handle),
      JSON.stringify(favorite),
    )

    favorite.action = 'added'
    if (window.debug) console.debug('[CustomerFavoritesService] add', favorite)

    const all = await this.getAll()
    this.events.trigger(this.addEventName, favorite, all)
    this.events.trigger(this.changedEventName, favorite, all)
  }

  /**
   * Add a product to the favorites
   * @param productHandle The handle of the product
   * @param variantId The id of the current selected variant
   */
  static async addProduct(productHandle: string, variantId?: number) {
    if (window.debug)
      console.debug(
        '[CustomerFavoritesService] addProduct',
        productHandle,
        variantId,
      )
    const additionalData = {
      variantId,
    }
    return this.add('product', productHandle, additionalData)
  }

  /**
   * Update a 'product' | 'collection' | 'page' | 'blog' | 'article' in the favorites
   * @param type The type of the favorite
   * @param handle The handle of the favorite
   * @param variantId The id of the current selected variant
   */
  static async update(
    type: CustomerFavoriteType,
    handle: string,
    additionalData: CustomerFavoriteData = {},
  ) {
    const favorite: CustomerFavorite = {
      type,
      handle,
      data: additionalData,
    }
    window.localStorage.setItem(
      this.getCacheKey(type, handle),
      JSON.stringify(favorite),
    )

    favorite.action = 'changed'
    if (window.debug)
      console.debug('[CustomerFavoritesService] update', favorite)

    const all = await this.getAll()
    this.events.trigger(this.updateEventName, favorite, all)
    this.events.trigger(this.changedEventName, favorite, all)
  }

  /**
   * Update a product to the favorites
   * @param productHandle The handle of the product
   * @param variantId The id of the new selected variant
   */
  static async updateProduct(productHandle: string, variantId?: number) {
    if (window.debug)
      console.debug(
        '[CustomerFavoritesService] addProduct',
        productHandle,
        variantId,
      )
    const additionalData = {
      variantId,
    }
    return this.update('product', productHandle, additionalData)
  }

  /**
   * Check if a favorite is already added to the favorites
   * @param type The type of the favorite
   * @param handle The handle of the favorite
   * @returns True if the favorite is already added to the favorites
   */
  static async isAdded(type: CustomerFavoriteType, handle: string) {
    const favoriteJson = window.localStorage.getItem(
      this.getCacheKey(type, handle),
    )
    if (!favoriteJson) {
      return false
    }
    const favorite: CustomerFavorite = JSON.parse(favoriteJson)
    const isAdded = !!favorite
    return isAdded
  }

  /**
   * Check if a product is already added to the favorites
   * @param productHandle The handle of the product
   * @returns True if the product is already added to the favorites
   */
  static async productIsAdded(productHandle: string) {
    return this.isAdded('product', productHandle)
  }

  /**
   * Remove a favorite from the favorites
   * @param type The type of the favorite
   * @param handle The handle of the product
   * @emits CustomerFavorites:remove
   */
  static async remove(type: CustomerFavoriteType, handle: string) {
    const favorite = await this.get(type, handle)
    if (!favorite) {
      return
    }
    favorite.action = 'removed'
    window.localStorage.removeItem(this.getCacheKey(type, handle))

    const all = await this.getAll()
    this.events.trigger(this.removeEventName, favorite, all)
    this.events.trigger(this.changedEventName, favorite, all)
  }

  /**
   * Remove a product from the favorites
   * @param productHandle The handle of the product
   * @emits CustomerFavorites:remove
   */
  static async removeProduct(productHandle: string) {
    const favorite = await this.get('product', productHandle)
    if (!favorite) {
      return
    }
    favorite.action = 'removed'
    window.localStorage.removeItem(this.getCacheKey('product', productHandle))

    const all = await this.getAll()
    this.events.trigger(this.removeEventName, favorite, all)
    this.events.trigger(this.changedEventName, favorite, all)
  }

  /**
   * Get a favorite by type and handle
   * @param type The type of the favorite
   * @param handle The handle of the favorite
   * @returns The favorite
   */
  static async get(
    type: CustomerFavoriteType,
    handle: string,
  ): Promise<CustomerFavorite | undefined> {
    const key = this.getCacheKey(type, handle)
    const favoriteJson = window.localStorage.getItem(key)
    if (!favoriteJson) {
      return undefined
    }
    const favorite: CustomerFavorite = JSON.parse(favoriteJson)
    return favorite
  }

  /**
   * Get all favorites optionally filtered by type
   * @param filterType The type of the favorites
   * @returns The (filtered) favorites
   */
  static async getAll(
    filterType?: CustomerFavoriteType,
  ): Promise<CustomerFavorite[]> {
    const favorites: CustomerFavorite[] = []

    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i)
      if (!key || !key.startsWith('favorite-')) {
        continue
      }
      const favoriteJson = window.localStorage.getItem(key)
      if (
        !favoriteJson ||
        (filterType && !key.startsWith(`favorite-${filterType}-`))
      ) {
        continue
      }
      const favorite: CustomerFavorite = JSON.parse(favoriteJson)
      favorites.push(favorite)
    }

    return favorites
  }

  /**
   * Get all products from the favorites
   * @returns All products from the favorites
   */
  static async getProducts(): Promise<CustomerFavoriteProduct[]> {
    const favoriteProducts: CustomerFavoriteProduct[] = []
    const favorites = await this.getAll('product')

    for (const favorite of favorites) {
      let product: ShopifyProduct | undefined = undefined
      try {
        const { product: preparedProduct } =
          await ShopifyProductService.getPreparedProductData(
            favorite.handle,
            favorite.data.variantId,
          )
        product = preparedProduct
      } catch (error) {
        console.error('[CustomerFavoritesService] getProducts', error)
        // Remove product from favorites if it doesn't exist anymore
        this.removeProduct(favorite.handle)
        continue
      }

      if (!product) {
        continue
      }

      favoriteProducts.push({
        ...favorite,
        product,
      } as CustomerFavoriteProduct)
    }
    return favoriteProducts
  }

  /**
   * Get a single product from the favorites
   * @param handle The handle of the product
   * @returns The product
   */
  static async getProduct(
    handle: string,
  ): Promise<CustomerFavoriteProduct | undefined> {
    const favorite = await this.get('product', handle)
    if (!favorite) {
      return undefined
    }

    let product: ShopifyProduct | undefined = undefined
    try {
      const { product: preparedProduct } =
        await ShopifyProductService.getPreparedProductData(
          favorite.handle,
          favorite.data.variantId,
        )
      product = preparedProduct
    } catch (error) {
      console.error('[CustomerFavoritesService] getProduct', error)
      return undefined
    }

    if (!product) {
      return undefined
    }

    return {
      ...favorite,
      type: 'product',
      product,
    } as CustomerFavoriteProduct
  }

  /**
   * Clear all favorites
   */
  static async clear(filterType?: CustomerFavoriteType) {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i)
      if (!key || !key.startsWith('favorite-')) {
        continue
      }
      const favoriteJson = window.localStorage.getItem(key)
      if (
        !favoriteJson ||
        (filterType && !key.startsWith(`favorite-${filterType}-`))
      ) {
        continue
      }
      window.localStorage.removeItem(key)
    }

    this.events.trigger(this.clearEventName)
    this.events.trigger(this.changedEventName, null, [])
  }

  /**
   * Count all favorites optionally filtered by type
   * @param filterType The type of the favorites
   * @returns The count of (filtered) favorites
   */
  static async count(filterType?: CustomerFavoriteType) {
    let count = 0
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i)
      if (!key || !key.startsWith('favorite-')) {
        continue
      }
      const favoriteJson = window.localStorage.getItem(key)
      if (
        !favoriteJson ||
        (filterType && !key.startsWith(`favorite-${filterType}-`))
      ) {
        continue
      }
      count++
    }
    return count
  }
}
